import React, { useState } from "react";
import { Popover, Paper, Typography, Box, Divider, LinearProgress, Chip, IconButton } from '@mui/material';
import { TYPE_INFO } from "assets/constants/Icons";

const ScreeningStatusCard = ({ screening }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    if (!screening) {
        return null;
    }

    const handleOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const getScore = (value) => Number(value || 0);

    const getStatusColor = (status) => {
        switch (status) {
            case "Selected":
                return "primary";

            case "Not selected":
                return "secondary";

            default:
                return "default";
        }
    };

    const renderScore = (label, value) => {
        const score = getScore(value);

        return (
            <Box mb={1.2}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.4}
                >
                    <Typography
                        variant="body2"
                        style={{
                            fontSize: 12,
                            color: "#555",
                            fontWeight: 500
                        }}
                    >
                        {label}
                    </Typography>

                    <Typography
                        variant="body2"
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#333"
                        }}
                    >
                        {score}/10
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={score * 10}
                    style={{
                        height: 6,
                        borderRadius: 5
                    }}
                />
            </Box>
        );
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                title="View Screening"
                style={{
                    padding: 3,
                    marginLeft: 5,
                    verticalAlign: "middle"
                }}
            >
                <i
                    className={TYPE_INFO}
                    style={{
                        fontSize: 20
                    }}
                />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
                PaperProps={{
                    style: {
                        marginTop: 6,
                        borderRadius: 10,
                        overflow: "hidden"
                    }
                }}
            >
                <Paper
                    elevation={4}
                    style={{
                        width: 320,
                        maxWidth: "calc(100vw - 30px)"
                    }}
                >
                    {/* Header */}
                    <Box
                        px={2}
                        py={1.5}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box>
                            <Typography
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#222"
                                }}
                            >
                                Screening
                            </Typography>
                        </Box>

                        <Chip
                            label={screening.screening_status || "Pending"}
                            color={getStatusColor(screening.screening_status)}
                            size="small"
                            style={{
                                height: 24,
                                fontSize: 11,
                                fontWeight: 600
                            }}
                        />
                    </Box>

                    <Divider />

                    {/* Score Section */}
                    <Box px={2} py={1.5}>
                        <Typography
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#888",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                marginBottom: 12
                            }}
                        >
                            Screening Score
                        </Typography>

                        {renderScore(
                            "Grooming",
                            screening.grooming_score
                        )}

                        {renderScore(
                            "Confidence",
                            screening.confidence_score
                        )}

                        {renderScore(
                            "Communication",
                            screening.communication_score
                        )}

                        {renderScore(
                            "Knowledge",
                            screening.knowledge_score
                        )}
                    </Box>

                    <Divider />

                    {/* Other Information */}
                    <Box px={2} py={1.5}>
                        <Box mb={1}>
                            <Typography
                                style={{
                                    fontSize: 11,
                                    color: "#999"
                                }}
                            >
                                Screening Date
                            </Typography>

                            <Typography
                                style={{
                                    fontSize: 12,
                                    color: "#333",
                                    fontWeight: 500
                                }}
                            >
                                {screening.screening_date
                                    ? new Date(
                                        screening.screening_date
                                    ).toLocaleDateString("en-GB")
                                    : "-"}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <Box>
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        color: "#999"
                                    }}
                                >
                                    Relocation
                                </Typography>

                                <Typography
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: "#333",
                                        textTransform: "capitalize"
                                    }}
                                >
                                    {screening.acceptance_to_relocate || "-"}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        color: "#999"
                                    }}
                                >
                                    Engagement
                                </Typography>

                                <Typography
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: "#333"
                                    }}
                                >
                                    {screening.engagement_type || "-"}
                                </Typography>
                            </Box>
                        </Box>

                        {screening.project_name && (
                            <Box mb={1}>
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        color: "#999"
                                    }}
                                >
                                    Assigned Project
                                </Typography>

                                <Typography
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: "#333"
                                    }}
                                >
                                    {screening.project_name}
                                </Typography>
                            </Box>
                        )}

                        {screening.screener_notes && (
                            <Box mt={1}>
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        color: "#999",
                                        marginBottom: 3
                                    }}
                                >
                                    Notes
                                </Typography>

                                <Box
                                    style={{
                                        background: "#f7f7f7",
                                        borderRadius: 6,
                                        padding: "7px 9px"
                                    }}
                                >
                                    <Typography
                                        style={{
                                            fontSize: 12,
                                            color: "#555",
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {screening.screener_notes}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Popover>
        </>
    );
};

export default ScreeningStatusCard;